import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { environment } from 'src/environments/environment';

export type SiteVisitPushEventType =
  | 'DOWNLOAD'
  | 'PREVIEW'
  | 'APPROVAL'
  | 'NOTIFICATION';

export interface SiteVisitPushEvent {
  event: SiteVisitPushEventType;
  url?: string;
  file_name?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private readonly subscribe_URL = '/usernotification/subscribe';

  constructor(
    private httpClient: HttpClient,
    private swPush: SwPush
  ) {}

  /** Whether Angular service worker push is active (typically production builds). */
  isPushEnabled(): boolean {
    return this.swPush.isEnabled;
  }

  /**
   * Subscribe for push, POST subscription to backend, then run callback.
   * Still runs callback if push subscription fails (API may return PDF URL directly).
   */
  ensurePushSubscriptionThen(run: () => void): void {
    if (!this.swPush.isEnabled) {
      run();
      return;
    }
    this.swPush
      .requestSubscription({
        serverPublicKey: environment.pushNotiPublicKey,
      })
      .then((sub) => {
        this.httpClient.post(this.subscribe_URL, sub).subscribe({
          next: () => run(),
          error: () => run(),
        });
      })
      .catch(() => run());
  }

  /**
   * Parse FCM / service-worker payloads (data may be on root or under notification).
   */
  parsePushMessagePayload(messages: any): SiteVisitPushEvent | null {
    if (!messages) {
      return null;
    }

    const data =
      messages?.data ??
      messages?.notification?.data ??
      (messages?.event ? messages : null);

    if (!data || typeof data !== 'object') {
      return null;
    }

    const event = String(data['event'] ?? data['type'] ?? '').toUpperCase();
    if (!event) {
      return null;
    }

    const rawUrl = data['url'] ?? data['pdfUrl'] ?? data['pdf_url'];
    const url =
      rawUrl === undefined || rawUrl === null
        ? undefined
        : this.safeDecodePushUrl(String(rawUrl));

    const file_name = data['file_name'] ?? data['fileName'] ?? data['filename'];

    return {
      event: event as SiteVisitPushEventType,
      url,
      file_name: file_name ? String(file_name) : undefined,
    };
  }

  safeDecodePushUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) {
      return trimmed;
    }
    try {
      return decodeURIComponent(trimmed);
    } catch {
      return trimmed;
    }
  }

  pushSubscription(): void {
    this.ensurePushSubscriptionThen(() => undefined);
  }

  /**
   * @deprecated Prefer app.component handling via parsePushMessagePayload + notificationClicks.
   */
  notificationClick(): void {
    this.swPush.notificationClicks.subscribe((arg) => {
      const evt = this.parsePushMessagePayload(arg?.notification ?? arg);
      if (!evt?.url) {
        return;
      }
      const decodedURL = evt.url;
      if (evt.event === 'DOWNLOAD') {
        const a = document.createElement('a');
        a.href = decodedURL;
        a.download = evt.file_name || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        window.open(decodedURL, '_blank');
      }
    });
  }
}
